import React, { useState, useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import Header from "../../../Common/Layout/Header";
import { useFlightSearch } from "../../hooks/useFlightSearch";
import styles from "./Flights4.module.css";
import {
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiArrowRightLine,
  RiFilterLine,
  RiTimeLine,
  RiMoneyDollarCircleLine,
  RiCheckLine,
  RiCloseLine,
  RiArrowDownSLine,
  RiSuitcaseLine,
  RiRefreshLine,
  RiCalendar2Line,
  RiUserLine,
  RiPlaneLine,
  RiInformationLine,
  RiAlertLine,
  RiMapPinLine
} from "react-icons/ri";

/* ─── Utility ─────────────────────────────────────────────────────────── */
const formatTime = (dt) => {
  if (!dt) return "--:--";
  try { return moment(dt).format("HH:mm"); } catch { return "--:--"; }
};

const formatDuration = (mins) => {
  if (!mins && mins !== 0) return "N/A";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m > 0 ? m + "m" : ""}`.trim();
};

const formatPrice = (val) => {
  if (!val && val !== 0) return "0";
  return Number(val).toLocaleString("en-IN");
};

const getMinPrice = (flightLeg) => {
  if (!flightLeg?.dcPriceDetails?.length) return 0;
  return Math.min(...flightLeg.dcPriceDetails.map((fare) => fare.totalPrice || Infinity));
};

const Flights4 = ({ mode, onNavigate, templateId, params: builderParams, styleConfig, companyId }) => {
  const urlParams = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const finalCompanyId = (mode === "builder" || mode === "preview")
    ? (user?.isSupplierAPI === true ? user?.cid : user?.parentCompanyId)
    : companyId;

  const themeVariables = {
    "--primary-color": styleConfig?.primary || "#C5A059",
    "--secondary-color": styleConfig?.secondary || "#E5D1B1",
  };

  const params = builderParams || urlParams;
  const { adults = 1, kids = 0, infants = 0, isDomestic = "DOMESTIC" } = params;
  const actualSearchQuery = mode === "builder" ? builderParams?.searchQuery : location.state?.searchQuery || {};
  
  const [searchQuery, setSearchQuery] = useState({
      ...actualSearchQuery,
      companyId: finalCompanyId || actualSearchQuery.companyId
  });

  useEffect(() => {
    if (!actualSearchQuery || Object.keys(actualSearchQuery).length === 0) {
      if (mode === "live") navigate("/template/flight/search");
    } else {
      setSearchQuery({ ...actualSearchQuery, companyId: finalCompanyId || actualSearchQuery.companyId });
    }
  }, [actualSearchQuery, mode, navigate, finalCompanyId]);

  const journeyType = searchQuery?.journeyType || "ONEWAY";
  const isRoundTrip = journeyType === "RETURN";

  /* ── State ── */
  const [activeTab, setActiveTab] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [expandTab, setExpandTab] = useState("fares");
  const [selectedFareIdxMap, setSelectedFareIdxMap] = useState({});
  const [selectedFaresRoundTrip, setSelectedFaresRoundTrip] = useState({});
  const [selectedFares, setSelectedFares] = useState({});

  const { data: flightData = [], isLoading: pageLoading } = useFlightSearch(searchQuery);

  /* ── Filters ── */
  const [filterStops, setFilterStops] = useState([]);
  const [filterAirlines, setFilterAirlines] = useState([]);
  const [filterMaxPrice, setFilterMaxPrice] = useState(null);
  const [filterTimeSlots, setFilterTimeSlots] = useState([]);
  const [sortBy, setSortBy] = useState("price");
  const [page, setPage] = useState(1);
  const PER_PAGE = 8;
  const currencySymbol = "₹"; 

  const activeFlights = useMemo(() => flightData[activeTab]?.dcFlightLegs || [], [flightData, activeTab]);

  const airlines = useMemo(() => {
    const map = {};
    activeFlights.forEach((f) => {
      const name = f.dcDisplayLegs?.[0]?.airlineData?.airline || "Unknown";
      map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map).map(([name, count]) => ({ name, count }));
  }, [activeFlights]);

  const maxPrice = useMemo(() => {
    if (!activeFlights.length) return 50000;
    return Math.max(...activeFlights.map((f) => getMinPrice(f)));
  }, [activeFlights]);

  useEffect(() => {
    if (maxPrice > 0 && filterMaxPrice === null) setFilterMaxPrice(maxPrice);
  }, [maxPrice, filterMaxPrice]);

  const filtered = useMemo(() => {
    let list = [...activeFlights];
    if (filterStops.length) list = list.filter((f) => filterStops.includes(f?.dcDisplayLegs?.[0]?.stops ?? 0));
    if (filterAirlines.length) list = list.filter((f) => filterAirlines.includes(f?.dcDisplayLegs?.[0]?.airlineData?.airline));
    if (filterMaxPrice !== null) list = list.filter((f) => getMinPrice(f) <= filterMaxPrice);
    if (filterTimeSlots.length) {
      list = list.filter((f) => {
        const dt = f?.dcDisplayLegs?.[0]?.startSegment?.departureDateTime;
        if (!dt) return false;
        const h = new Date(dt).getHours();
        return filterTimeSlots.some((slot) => {
          if (slot === "early") return h >= 0 && h < 6;
          if (slot === "morn") return h >= 6 && h < 12;
          if (slot === "after") return h >= 12 && h < 18;
          if (slot === "eve") return h >= 18;
          return false;
        });
      });
    }
    list.sort((a, b) => {
      if (sortBy === "price") return getMinPrice(a) - getMinPrice(b);
      if (sortBy === "duration") return (a?.dcDisplayLegs?.[0]?.totalDuration || 0) - (b?.dcDisplayLegs?.[0]?.totalDuration || 0);
      return 0;
    });
    return list;
  }, [activeFlights, filterStops, filterAirlines, filterMaxPrice, filterTimeSlots, sortBy]);

  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  const routeToDetails = (payload, passedJourneyType) => {
    if (mode === "builder") onNavigate({ name: "flight-details", params: { searchQuery, payload, journeyType: passedJourneyType } });
    else navigate(`/template/flight-details/${adults}/${kids}/${infants}/${isDomestic}`, { state: { searchQuery, payload, journeyType: passedJourneyType } });
  };

  const handleBookFlight = (flightLeg, e) => {
    e?.stopPropagation();
    const isOutbound = activeTab === 0;
    const currentJourneyType = flightData[activeTab]?.journeyType || "ONEWAY";
    const selectedFareIdx = selectedFareIdxMap[flightLeg.supplierLegId] ?? 0;
    const cheapestFare = flightLeg.dcPriceDetails?.[selectedFareIdx] || [...flightLeg.dcPriceDetails].sort((a, b) => a.totalPrice - b.totalPrice)[0];

    if (!isRoundTrip) {
      routeToDetails([{ flightLeg, fare: cheapestFare, journeyType: currentJourneyType }], currentJourneyType);
    } else {
      const routeKey = isOutbound ? "outbound" : "return";
      setSelectedFaresRoundTrip((prev) => ({
        ...prev,
        [routeKey]: { flightLeg, fare: cheapestFare, journeyType: currentJourneyType, locationFrom: flightLeg?.dcDisplayLegs?.[0]?.startSegment?.airportCode, locationTo: flightLeg?.dcDisplayLegs?.[0]?.endSegment?.airportCode }
      }));
      if (flightData.length > 1) setActiveTab(isOutbound ? 1 : 0);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId((p) => (p === id ? null : id));
    setExpandTab("fares");
  };

  const origin = searchQuery?.flightTravelerPoints?.[0]?.from || "—";
  const destination = searchQuery?.flightTravelerPoints?.[0]?.to || "—";
  const depDate = searchQuery?.flightTravelerPoints?.[0]?.departureDate || "";
  const totalPax = Number(adults) + Number(kids) + Number(infants);

  return (
    <div className={styles.page} style={themeVariables}>
      {/* ── Top Bar ── */}
      <div className={styles.topBar}>
        <div className={styles.topBarInner}>
          <div className={styles.routeBox}>
            <RiMapPinLine className={styles.routeIcon} />
            <span className={styles.city}>{origin}</span>
            <RiArrowRightLine className={styles.cityArrow} />
            <span className={styles.city}>{destination}</span>
          </div>
          <div className={styles.metaRow}>
            <span className={styles.metaPill}><RiCalendar2Line /> {moment(depDate).format("DD MMM YY")}</span>
            <span className={styles.metaPill}><RiUserLine /> {totalPax} Guest{totalPax !== 1 ? "s" : ""}</span>
            <span className={styles.metaPill}><RiPlaneLine /> {searchQuery?.cabinClass || "ECONOMY"}</span>
          </div>
          <button className={styles.modifyBtn} onClick={() => navigate(-1)}><RiFilterLine /> Modify</button>
        </div>
      </div>

      <div className={styles.main}>
        {/* ── Sidebar ── */}
        <aside className={styles.sidebar}>
          <div className={styles.filterCard}>
             <div className={styles.groupTitle}>Price Range</div>
             <div className={styles.priceMeta}>
               <span>{currencySymbol}0</span>
               <span className={styles.goldText}>{currencySymbol}{formatPrice(filterMaxPrice)}</span>
             </div>
             <input type="range" className={styles.slider} min={0} max={maxPrice} value={filterMaxPrice ?? maxPrice} onChange={(e) => setFilterMaxPrice(Number(e.target.value))} />
          </div>

          <div className={styles.filterCard}>
            <div className={styles.groupTitle}>Stops</div>
            {[{v:0, l:"Non-Stop"}, {v:1, l:"1 Stop"}, {v:2, l:"2+ Stops"}].map(s => (
              <label key={s.v} className={styles.checkRow}>
                <input type="checkbox" checked={filterStops.includes(s.v)} onChange={() => setFilterStops(p => p.includes(s.v) ? p.filter(x => x!==s.v) : [...p, s.v])} />
                <span className={styles.customCheck}><RiCheckLine /></span> {s.l}
              </label>
            ))}
          </div>

          <div className={styles.filterCard}>
            <div className={styles.groupTitle}>Airlines</div>
            {airlines.map(a => (
              <label key={a.name} className={styles.checkRow}>
                <input type="checkbox" checked={filterAirlines.includes(a.name)} onChange={() => setFilterAirlines(p => p.includes(a.name) ? p.filter(x => x!==a.name) : [...p, a.name])} />
                <span className={styles.customCheck}><RiCheckLine /></span> {a.name}
              </label>
            ))}
          </div>
        </aside>

        {/* ── Results ── */}
        <div className={styles.resultsPanel}>
          <div className={styles.resultsHeader}>
            <div className={styles.countText}>Showing <span className={styles.goldText}>{filtered.length} Flights</span> found</div>
            
            {isRoundTrip && flightData.length > 1 && (
              <div className={styles.routeTabs}>
                {flightData.map((item, idx) => (
                  <button key={idx} className={`${styles.routeTab} ${activeTab === idx ? styles.routeTabActive : ""}`} onClick={() => setActiveTab(idx)}>
                    {idx === 0 ? "Outbound" : "Return"}
                  </button>
                ))}
              </div>
            )}

            <div className={styles.sortGroup}>
              <span className={styles.sortLabel}>Sort:</span>
              <button className={`${styles.sortTab} ${sortBy === 'price' ? styles.sortTabActive : ''}`} onClick={() => setSortBy('price')}>Price</button>
              <button className={`${styles.sortTab} ${sortBy === 'duration' ? styles.sortTabActive : ''}`} onClick={() => setSortBy('duration')}>Fastest</button>
            </div>
          </div>

          {pageLoading ? (
            <div className={styles.loadingArea}><div className={styles.spin}></div>Searching...</div>
          ) : (
            paginated.map((flightLeg, idx) => {
              const displayLeg = flightLeg?.dcDisplayLegs?.[0];
              const priceDetails = flightLeg?.dcPriceDetails || [];
              const selectedFareIdx = selectedFareIdxMap[flightLeg.supplierLegId] ?? 0;
              const fare = priceDetails[selectedFareIdx] || priceDetails[0];
              const isExpanded = expandedId === flightLeg.supplierLegId;
              const isSelected = isRoundTrip ? (activeTab === 0 ? selectedFaresRoundTrip.outbound?.flightLeg?.supplierLegId === flightLeg.supplierLegId : selectedFaresRoundTrip.return?.flightLeg?.supplierLegId === flightLeg.supplierLegId) : false;

              return (
                <div key={flightLeg.supplierLegId || idx} className={`${styles.flightCard} ${isSelected ? styles.cardSelected : ''}`}>
                  <div className={styles.cardMain} onClick={() => toggleExpand(flightLeg.supplierLegId)}>
                    <div className={styles.airlinePart}>
                      <div className={styles.logoBox}>
                        <img src={`/assets/images/airlinelogos/${displayLeg?.airlineData?.airlineCode}.png`} alt="Logo" onError={(e) => {e.target.style.display="none"}} />
                      </div>
                      <div className={styles.airlineName}>{displayLeg?.airlineData?.airline}</div>
                      <div className={styles.flightCode}>{displayLeg?.airlineData?.flightNumber}</div>
                    </div>

                    <div className={styles.routePart}>
                      <div className={styles.point}>
                        <div className={styles.time}>{formatTime(displayLeg?.startSegment?.departureDateTime)}</div>
                        <div className={styles.airport}>{displayLeg?.startSegment?.airportCode}</div>
                      </div>
                      <div className={styles.path}>
                        <div className={styles.duration}>{formatDuration(displayLeg?.totalDuration)}</div>
                        <div className={styles.visualPath}>
                          <div className={styles.dot}></div>
                          <div className={styles.line}><RiPlaneLine className={styles.planeIcon} /></div>
                          <div className={styles.dot}></div>
                        </div>
                        <div className={styles.stops}>{displayLeg?.stops === 0 ? 'Non-stop' : `${displayLeg.stops} Stop`}</div>
                      </div>
                      <div className={styles.point}>
                        <div className={styles.time}>{formatTime(displayLeg?.endSegment?.arrivalDateTime)}</div>
                        <div className={styles.airport}>{displayLeg?.endSegment?.airportCode}</div>
                      </div>
                    </div>

                    <div className={styles.pricePart}>
                      <div className={styles.price}>{currencySymbol}{formatPrice(fare?.totalPrice)}</div>
                      <div className={styles.fareTypeLabel}>{fare?.fareIdentifier || "PUBLISHED"}</div>
                      <button className={styles.bookBtn} onClick={(e) => handleBookFlight(flightLeg, e)}>
                        {isRoundTrip ? (isSelected ? 'Selected' : 'Select') : 'Book Now'}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className={styles.expandedDetails}>
                       <div className={styles.expandTabs}>
                          <button className={`${styles.exTab} ${expandTab === 'fares' ? styles.exTabActive : ''}`} onClick={() => setExpandTab('fares')}>Fare Options</button>
                          <button className={`${styles.exTab} ${expandTab === 'baggage' ? styles.exTabActive : ''}`} onClick={() => setExpandTab('baggage')}>Baggage Info</button>
                          <button className={`${styles.exTab} ${expandTab === 'itinerary' ? styles.exTabActive : ''}`} onClick={() => setExpandTab('itinerary')}>Flight Details</button>
                       </div>

                       {/* Fares Tab */}
                       {expandTab === 'fares' && (
                         <div className={styles.fareGrid}>
                            {priceDetails.map((f, i) => (
                              <div key={i} className={`${styles.fareCard} ${selectedFareIdx === i ? styles.fareCardSelected : ""}`} onClick={() => setSelectedFareIdxMap({...selectedFareIdxMap, [flightLeg.supplierLegId]: i})}>
                                <div className={styles.fareName}>{f.fareIdentifier}</div>
                                <div className={styles.farePrice}>{currencySymbol}{formatPrice(f.totalPrice)}</div>
                                <div className={styles.fareFeatures}>
                                  <div className={styles.feature}><RiSuitcaseLine /> {f.checkinBag || '20 Kg'} Check-in</div>
                                  <div className={styles.feature}><RiSuitcaseLine /> {f.cabinBaggage || '7 Kg'} Cabin</div>
                                  <div className={styles.feature}>
                                    {f.dcAdults?.mealType === "Free Meal" ? <RiCheckLine className={styles.ok}/> : <RiCloseLine className={styles.no}/>} Meal
                                  </div>
                                  <div className={styles.feature}>
                                    {f.dcAdults?.refundable === "Refundable" ? <RiCheckLine className={styles.ok}/> : <RiCloseLine className={styles.no}/>} Refundable
                                  </div>
                                </div>
                              </div>
                            ))}
                         </div>
                       )}

                       {/* Baggage Tab */}
                       {expandTab === 'baggage' && (
                        <div className={styles.baggageRow}>
                          <div className={styles.bagItem}>
                            <div className={styles.bagIcon}>🧳</div>
                            <div>
                              <div className={styles.bagLabel}>Check-in Baggage</div>
                              <div className={styles.bagVal}>{fare?.checkinBag || "20 Kg"}</div>
                            </div>
                          </div>
                          <div className={styles.bagItem}>
                            <div className={styles.bagIcon}>🎒</div>
                            <div>
                              <div className={styles.bagLabel}>Cabin Baggage</div>
                              <div className={styles.bagVal}>{fare?.cabinBaggage || "7 Kg"}</div>
                            </div>
                          </div>
                          <div className={styles.bagItem}>
                            <div className={styles.bagIcon}>🍽️</div>
                            <div>
                              <div className={styles.bagLabel}>Meal Type</div>
                              <div className={styles.bagVal}>{fare?.dcAdults?.mealType === "Free Meal" ? "Included" : "Paid"}</div>
                            </div>
                          </div>
                        </div>
                       )}

                       {/* Itinerary Tab */}
                       {expandTab === 'itinerary' && (
                         <div className={styles.itineraryView}>
                            {flightLeg.dcSegments?.map((seg, sIdx) => (
                              <React.Fragment key={sIdx}>
                               <div className={styles.segmentRow}>
                                  <div className={styles.segAirline}>
                                    <img src={`/assets/images/airlinelogos/${seg?.tFAirlineData?.airlineCode}.png`} alt="Logo" width="30"/>
                                    <span>{seg?.tFAirlineData?.airline} · {seg?.tFAirlineData?.flightNumber}</span>
                                  </div>
                                  <div className={styles.segTimes}>
                                    <div className={styles.segPoint}>
                                      <strong>{formatTime(seg.tFDepartureData.departureDateTime)}</strong>
                                      <span>{seg.tFDepartureData.airportCode}</span>
                                    </div>
                                    <div className={styles.segDur}>{formatDuration(seg.tFAirlineData.duration)}</div>
                                    <div className={styles.segPoint}>
                                      <strong>{formatTime(seg.tFArrivalData.arrivalDateTime)}</strong>
                                      <span>{seg.tFArrivalData.airportCode}</span>
                                    </div>
                                  </div>
                               </div>
                               {sIdx < flightLeg.dcSegments.length - 1 && (
                                <div className={styles.layoverAlert}>
                                  <RiTimeLine /> Layover in {flightLeg.dcSegments[sIdx]?.tFArrivalData?.city}: {formatDuration(flightLeg.dcSegments[sIdx + 1]?.tFAirlineData?.connectingTime)}
                                </div>
                               )}
                              </React.Fragment>
                            ))}
                         </div>
                       )}
                    </div>
                  )}

                  <div className={styles.toggleStrip} onClick={(e) => { e.stopPropagation(); toggleExpand(flightLeg.supplierLegId); }}>
                      <span>{isExpanded ? "Hide Details" : "View Details"}</span>
                      <RiArrowDownSLine className={isExpanded ? styles.rot : ""} />
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Flights4;